/* eslint-disable max-len */
import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Spin,
  DatePicker,
  Input,
  Button,
  Tag,
  Space,
  Modal,
  Avatar,
  Typography,
  Tooltip,
  Divider,
  Badge,
  message,
} from 'antd';
import {
  SearchOutlined,
  UndoOutlined,
  EyeOutlined,
  UserOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import 'moment/locale/es';
import Select from 'components/Select';
import Table from 'components/Table';
import { getBitacoraGlobal, getModelosAuditados, exportarBitacoraGlobal } from 'api/configuracion/bitacora';
import { checkToken } from 'api/utils';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

export const bitacoraAuditoriaPermission = {
  permissionModel: 'logentry',
  catalogsModels: [],
  tabsModels: [],
};

const actionTagConfig = {
  0: { color: 'green', text: 'Creación' },
  1: { color: 'blue', text: 'Modificación' },
  2: { color: 'red', text: 'Eliminación' },
};

const tiposDeAccion = [
  { id: 0, descripcion: 'Creación' },
  { id: 1, descripcion: 'Modificación' },
  { id: 2, descripcion: 'Eliminación' },
];

const BitacoraAuditoria = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageActual, setPageActual] = useState(1);
  const pageSize = 50;
  const [modelosAuditados, setModelosAuditados] = useState([]);

  // Filtros
  const [fechas, setFechas] = useState([moment().subtract(1, 'days'), moment().add(1, 'days')]);
  const [selectedModelo, setSelectedModelo] = useState(undefined);
  const [selectedAction, setSelectedAction] = useState(undefined);
  const [searchText, setSearchText] = useState('');

  // Modal de detalle
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Guarda contra condición de carrera: si el usuario cambia de filtro antes
  // de que la petición anterior responda (p. ej. la carga inicial con el
  // rango por defecto sigue en vuelo cuando ya se disparó la del nuevo rango
  // elegido), la respuesta que llegue más tarde pisaba los datos de la más
  // reciente sin importar cuál se inició después. Solo se aplica la
  // respuesta de la última petición realmente iniciada.
  const bitacoraRequestIdRef = useRef(0);

  // Combina modelo Django + página del frontend en una sola etiqueta para el
  // selector de filtro (ej. "Asignación de Plazas (Administraciondeplaza)"),
  // igual que se muestra en la tabla y en el modal de detalle -- así el
  // técnico puede ubicar la pantalla real sin tener que conocer el modelo.
  const conEtiquetaCombinada = (modelos) => modelos.map((m) => ({
    ...m,
    etiqueta: m.page ? `${m.page} (${m.name})` : m.name,
  }));

  const fetchModelos = async () => {
    try {
      const modelos = await getModelosAuditados();
      if (Array.isArray(modelos) && modelos.length > 0) {
        setModelosAuditados(conEtiquetaCombinada(modelos));
      } else {
        // Modelos por defecto
        setModelosAuditados(conEtiquetaCombinada([
          { model: 'empleado', name: 'Empleado', page: 'Empleados' },
          { model: 'plaza', name: 'Plaza', page: 'Plazas' },
          { model: 'nombramiento', name: 'Nombramiento', page: 'Nombramientos' },
          { model: 'incidenciasempleados', name: 'Incidencias de Empleados', page: 'Registro de Incidencias' },
          { model: 'percepciones', name: 'Percepciones', page: 'Configuración de Percepciones' },
          { model: 'deducciones', name: 'Deducciones', page: 'Configuración de Deducciones' },
          { model: 'descuentos', name: 'Descuentos', page: 'Configuración de Descuentos' },
          { model: 'usuario', name: 'Usuario', page: 'Usuarios' },
        ]));
      }
    } catch {
      // ignore
    }
  };

  const fetchBitacora = useCallback(async (page = 1) => {
    // eslint-disable-next-line no-plusplus
    const requestId = ++bitacoraRequestIdRef.current;
    setLoading(true);
    await checkToken();

    // `page`/`page_size` son obligatorios para que el backend pagine
    // (`BitacoraPagination` acota la respuesta a 50 registros por página en
    // vez de regresar de golpe todo el rango de fechas filtrado, que con
    // rangos amplios puede ser de cientos de miles de filas).
    const params = { page, page_size: pageSize };

    if (fechas && fechas[0] && fechas[1]) {
      params.fecha_inicio = fechas[0].format('YYYY-MM-DD');
      params.fecha_fin = fechas[1].format('YYYY-MM-DD');
    }
    if (selectedModelo) {
      params.modelo = selectedModelo;
    }
    if (selectedAction !== undefined) {
      params.action = selectedAction;
    }
    if (searchText.trim()) {
      params.search = searchText.trim();
    }

    const response = await getBitacoraGlobal(params);

    // Si mientras esta petición estaba en vuelo se disparó otra más reciente
    // (nuevo filtro, otra página), esta respuesta ya quedó obsoleta -- no
    // pisar los datos de la petición vigente aunque haya llegado después.
    if (requestId !== bitacoraRequestIdRef.current) {
      return;
    }

    if (Array.isArray(response)) {
      setData(response);
      setTotalCount(response.length);
    } else if (Array.isArray(response?.results)) {
      setData(response.results);
      setTotalCount(response.count || 0);
    } else {
      setData([]);
      setTotalCount(0);
    }
    setPageActual(page);
    setLoading(false);
  }, [fechas, selectedModelo, selectedAction, searchText]);

  useEffect(() => {
    fetchModelos();
  }, []);

  useEffect(() => {
    fetchBitacora();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validarRangoDeFechas = () => {
    if (!fechas || !fechas[0] || !fechas[1]) {
      message.error('El Rango de Fechas es obligatorio para realizar la búsqueda.');
      return false;
    }
    return true;
  };

  const handleSearch = () => {
    if (!validarRangoDeFechas()) return;
    fetchBitacora();
  };

  const handleReset = () => {
    setFechas([moment().subtract(1, 'days'), moment().add(1, 'days')]);
    setSelectedModelo(undefined);
    setSelectedAction(undefined);
    setSearchText('');
  };

  // Quita acentos/diacríticos para que el nombre de archivo quede limpio
  // (p. ej. "Modificación" -> "Modificacion").
  const limpiarParaNombreArchivo = (texto) => texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '');

  // Exportación generada en el SERVIDOR (ver `BitacoraAuditoriaViewSet.exportar`):
  // `data` en el cliente solo tiene la página actual (50 filas) desde que se
  // forzó la paginación del listado, así que ya no sirve como fuente para
  // exportar "todo lo filtrado" -- se le pasan los mismos filtros al backend
  // y este arma el .xlsx completo sin paginar (con su propio tope de filas).
  const handleExportar = async () => {
    if (!validarRangoDeFechas()) return;

    const params = {
      fecha_inicio: fechas[0].format('YYYY-MM-DD'),
      fecha_fin: fechas[1].format('YYYY-MM-DD'),
    };
    if (selectedModelo) params.modelo = selectedModelo;
    if (selectedAction !== undefined) params.action = selectedAction;
    if (searchText.trim()) params.search = searchText.trim();

    setLoading(true);
    const { blob, error } = await exportarBitacoraGlobal(params);
    setLoading(false);

    if (error) {
      message.error(error);
      return;
    }

    // Nombre del archivo: Bitacora_de_Auditoria_<RangoDeFechas>[_<Modulo>][_<TipoDeAccion>]
    // -- Módulo y Tipo de Acción solo se agregan si el usuario los seleccionó.
    const rangoDeFechas = `${fechas[0].format('DDMMYYYY')}-${fechas[1].format('DDMMYYYY')}`;
    const nombrePartes = ['Bitacora_de_Auditoria', rangoDeFechas];

    const moduloNombre = modelosAuditados.find((m) => m.model === selectedModelo)?.name;
    if (moduloNombre) nombrePartes.push(limpiarParaNombreArchivo(moduloNombre));

    const accionNombre = tiposDeAccion.find((a) => a.id === selectedAction)?.descripcion;
    if (accionNombre) nombrePartes.push(limpiarParaNombreArchivo(accionNombre));

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${nombrePartes.join('_')}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      titleText: 'Fecha y Hora',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 170,
      render: (val) => (
        <div>
          <Text strong style={{ fontSize: 13, display: 'block' }}>
            {moment(val).format('DD/MM/YYYY')}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {moment(val).format('hh:mm:ss a')}
          </Text>
        </div>
      ),
    },
    {
      titleText: 'Usuario Responsable',
      dataIndex: 'actor_nombre',
      key: 'actor_nombre',
      width: 220,
      render: (val, record) => (
        <Space align="center" size="small">
          <Avatar
            size="small"
            icon={<UserOutlined />}
            style={{ backgroundColor: val === 'Sistema' ? '#faad14' : '#1890ff' }}
          />
          <div>
            <Text strong style={{ fontSize: 12, display: 'block' }}>
              {val || 'Sistema'}
            </Text>
            {record.actor_email && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {record.actor_email}
              </Text>
            )}
          </div>
        </Space>
      ),
    },
    {
      titleText: 'Módulo / Entidad',
      dataIndex: 'content_type_page',
      key: 'content_type_page',
      width: 190,
      // `content_type_page` es el nombre de la pantalla en NOMINAS-FRONTEND
      // (ver `configuracion/utils/modelo_a_pagina_frontend.py` en el backend);
      // `content_type_name` es el nombre técnico del modelo Django. Se
      // muestran juntos para que un técnico ubique la pantalla real sin
      // tener que conocer el modelo. Si el modelo todavía no está mapeado
      // (content_type_page=None), se muestra solo el nombre técnico, igual
      // que antes de este cambio.
      render: (val, record) => (
        <div>
          <Tag color="geekblue" style={{ fontWeight: 500 }}>
            {val || record.content_type_name || 'Entidad'}
          </Tag>
          {val && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
              {record.content_type_name}
            </Text>
          )}
        </div>
      ),
    },
    {
      titleText: 'Registro Afectado',
      dataIndex: 'object_repr',
      key: 'object_repr',
      // `width` es obligatorio aquí: `components/Table.jsx` calcula `scroll.x` sumando
      // solo las columnas que ya traen `width` (ver `getColWidth`/el `useEffect` que arma
      // `scrollX`) -- una columna sin `width` queda fuera de esa suma, por lo que AntD
      // termina creyendo que el ancho total de la tabla es el de las demás columnas y la
      // aprieta a un ancho casi cero (confirmado: 0.06px), partiendo el texto letra por
      // letra. Nunca se notó porque esta página no cargaba datos reales antes del fix de
      // rendimiento de la bitácora (quedaba cargando indefinidamente).
      width: 300,
      render: (val, record) => (
        <div>
          <Text strong style={{ fontSize: 13, display: 'block' }}>
            {val || `ID: ${record.object_id || record.object_pk}`}
          </Text>
          {record.cambios_formateados && record.cambios_formateados.length > 0 && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.cambios_formateados.length}
              {' '}
              {record.cambios_formateados.length === 1 ? 'campo modificado' : 'campos modificados'}
              {' '}
              (
              {record.cambios_formateados.map((c) => c.etiqueta).slice(0, 3).join(', ')}
              {record.cambios_formateados.length > 3 ? '...' : ''}
              )
            </Text>
          )}
        </div>
      ),
    },
    {
      titleText: 'Acción',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (val, record) => {
        // Un borrado lógico (eliminado=True) llega con action=1 (Modificación) --
        // se muestra con el mismo color/etiqueta de Eliminación. Ver es_eliminacion_logica
        // en LogEntrySerializer (backend).
        const metaKey = record.es_eliminacion_logica ? 2 : val;
        const meta = actionTagConfig[metaKey] || { color: 'default', text: record.action_display || 'Acción' };
        return <Tag color={meta.color}>{record.action_display || meta.text}</Tag>;
      },
    },
    {
      titleText: 'IP Origen',
      dataIndex: 'remote_addr',
      key: 'remote_addr',
      width: 130,
      render: (val) => (
        val ? (
          <Tag icon={<GlobalOutlined />} style={{ fontSize: 11 }}>
            {val}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        )
      ),
    },
    {
      titleText: 'Detalle',
      key: 'acciones',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="Ver detalle de cambios">
          <Button
            type="primary"
            ghost
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedEntry(record);
              setDetailVisible(true);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  const renderDesgloseCampos = () => {
    if (selectedEntry.action === 0) {
      return (
        <div style={{
          background: '#f6ffed', padding: '12px 16px', borderRadius: 8, border: '1px solid #b7eb8f',
        }}
        >
          <Text style={{ color: '#389e0d' }}>
            Este evento corresponde a la
            {' '}
            <strong>Creación inicial</strong>
            {' '}
            del registro en la base de datos.
          </Text>
        </div>
      );
    }
    // El sistema nunca ejecuta un DELETE SQL real (todo registro se borra marcando
    // eliminado=True vía .save(), ver sac/mixins/views.py en el backend) -- por eso un
    // borrado lógico llega aquí con action=1 (Modificación), nunca action=2. El backend
    // ya resuelve esto en es_eliminacion_logica (LogEntrySerializer).
    const esEliminacion = selectedEntry.action === 2 || selectedEntry.es_eliminacion_logica;
    const bannerEliminacion = esEliminacion ? (
      <div style={{
        background: '#fff2f0', padding: '12px 16px', borderRadius: 8, border: '1px solid #ffccc7', marginBottom: 12,
      }}
      >
        <Text style={{ color: '#cf1322' }}>
          Este evento corresponde a la
          {' '}
          <strong>Eliminación</strong>
          {' '}
          del registro
          {selectedEntry.action !== 2 ? ' (borrado lógico)' : ''}
          .
        </Text>
      </div>
    ) : null;

    if (selectedEntry.action === 2) {
      return bannerEliminacion;
    }
    return (
      <div>
        {bannerEliminacion}
        {selectedEntry.cambios_formateados && selectedEntry.cambios_formateados.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {selectedEntry.cambios_formateados.map((cambio) => (
              <div
                key={cambio.etiqueta}
                style={{
                  background: '#f9fbfd',
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid #e8e8e8',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 8,
                  maxWidth: '100%',
                }}
              >
                <span style={{
                  fontWeight: 600, color: '#1f1f1f', minWidth: 150, flexShrink: 0,
                }}
                >
                  {cambio.etiqueta}
                </span>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  minWidth: 0,
                  maxWidth: '100%',
                  flex: '1 1 auto',
                }}
                >
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%',
                  }}
                  >
                    <Badge status="error" />
                    <Tag
                      color="default"
                      style={{
                        textDecoration: 'line-through',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                      }}
                    >
                      {cambio.anterior}
                    </Tag>
                  </div>
                  <span style={{ color: '#1890ff', fontWeight: 'bold' }}>➔</span>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%',
                  }}
                  >
                    <Badge status="success" />
                    <Tag
                      color="cyan"
                      style={{
                        fontWeight: 500,
                        color: '#0050b3',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                      }}
                    >
                      {cambio.nuevo}
                    </Tag>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Text type="secondary" style={{ fontStyle: 'italic' }}>
            No hay cambios en campos específicos registrados para este evento.
          </Text>
        )}
      </div>
    );
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Card
          className="form-container"
          title="Bitácora de Auditoría del Sistema"
          bordered={false}
        >
          <Form layout="vertical">
            <Row gutter={10}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Rango de Fechas" required>
                  <RangePicker
                    value={fechas}
                    onChange={setFechas}
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Módulo / Entidad">
                  <Select
                    dataSource={modelosAuditados}
                    valueProp="model"
                    labelProp="etiqueta"
                    value={selectedModelo}
                    onChange={setSelectedModelo}
                    allowClear
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={5}>
                <Form.Item label="Tipo de Acción">
                  <Select
                    dataSource={tiposDeAccion}
                    keyProp="id"
                    labelProp="descripcion"
                    value={selectedAction}
                    onChange={setSelectedAction}
                    allowClear
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={7}>
                <Form.Item label="Buscar por Texto / Objeto">
                  <Input
                    placeholder="Nombre, folio, usuario..."
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onPressEnter={handleSearch}
                    allowClear
                  />
                </Form.Item>
              </Col>

            </Row>
          </Form>

          <Divider style={{ margin: '12px 0 20px 0' }} />

          <Table
            cols={columns}
            data={data}
            loading={loading}
            rowKey="id"
            allowExportParam={false}
            allowExportExcel={false}
            allowSearch={false}
            // `pagination` por default es `true` en este componente (modo
            // cliente: pagina lo que ya esté en `data`). Como aquí `data`
            // solo trae la página actual (50 filas) del servidor, hay que
            // desactivarlo explícitamente para que se use el bloque de
            // paginación de servidor (`count`/`paginationCallback` abajo) --
            // sin esto, la paginación visible ignoraba el total real y
            // mostraba solo las páginas que entraban en esas 50 filas.
            pagination={false}
            count={totalCount}
            currentPage={pageActual}
            paginationCallback={fetchBitacora}
            pageSize={pageSize}
            customActions={[
              {
                onClick: handleExportar, icon: <FileExcelOutlined />, text: 'Exportar', disabledBySelection: false,
              },
              {
                onClick: handleSearch, icon: <SearchOutlined />, text: 'Filtrar', disabledBySelection: false,
              },
              {
                onClick: handleReset, icon: <UndoOutlined />, text: 'Limpiar', disabledBySelection: false,
              },
            ]}
          />
        </Card>

        {/* Modal de Detalle de Cambios */}
        <Modal
          title={(
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClockCircleOutlined style={{ color: '#1890ff' }} />
              <span>
                Detalle de Entrada de Auditoría #
                {selectedEntry?.id}
              </span>
            </div>
          )}
          visible={detailVisible}
          onCancel={() => setDetailVisible(false)}
          footer={[
            <Button key="cerrar" type="primary" onClick={() => setDetailVisible(false)}>
              Cerrar
            </Button>,
          ]}
          width={680}
        >
          {selectedEntry && (
            <div>
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Fecha y Hora:</Text>
                  <div>
                    <Text strong>
                      {moment(selectedEntry.timestamp).format('DD [de] MMMM [de] YYYY, hh:mm:ss a')}
                    </Text>
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Acción Realizada:</Text>
                  <div>
                    {/* Mismo criterio que la columna "Acción" de la tabla: un borrado
                        lógico llega con action=1 (Modificación), así que el color debe
                        tomarse como Eliminación (2) para no contradecir action_display. */}
                    <Tag color={actionTagConfig[selectedEntry.es_eliminacion_logica ? 2 : selectedEntry.action]?.color || 'blue'}>
                      {selectedEntry.action_display}
                    </Tag>
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Usuario Responsable:</Text>
                  <div>
                    <Text strong>{selectedEntry.actor_nombre || 'Sistema'}</Text>
                    {selectedEntry.actor_email && (
                      <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                        {selectedEntry.actor_email}
                      </Text>
                    )}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Dirección IP:</Text>
                  <div>
                    <Tag icon={<GlobalOutlined />}>{selectedEntry.remote_addr || 'Desconocida'}</Tag>
                  </div>
                </Col>
                <Col span={24}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Módulo y Registro:</Text>
                  <div>
                    <Tag color="geekblue">
                      {selectedEntry.content_type_page || selectedEntry.content_type_name}
                    </Tag>
                    {selectedEntry.content_type_page && (
                      <Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>
                        (
                        {selectedEntry.content_type_name}
                        )
                      </Text>
                    )}
                    <Text strong>{selectedEntry.object_repr}</Text>
                  </div>
                </Col>
              </Row>

              <Divider style={{ margin: '16px 0' }} />

              <Title level={5} style={{ marginBottom: 12 }}>
                Desglose de Campos Modificados:
              </Title>

              {renderDesgloseCampos()}
            </div>
          )}
        </Modal>
      </Spin>
    </Row>
  );
};

export default BitacoraAuditoria;
